import MuxPlayer from '@mux/mux-player-react';
import { useLocalPreviewVideoUrl } from '@src/hooks/useLocalPreviewVideoUrl';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useEffect, useRef, useState } from 'react';
import { VideoSeekSlider } from 'react-video-seek-slider';
import styled from 'styled-components';

import CirclePlayFilledIcon from '../../svgs/CirclePlayFilledIcon';
import { resolveClipPlayerSource } from '../../utility/clipPlayerSource';
import { extractErrorInfo } from '../../utility/error';
import {
  getMuxHostedThumbnailUrl,
  getSelfHostedThumbnailUrl,
} from '../../utility/story';
import { isCompressedRenditionPending } from '../../utility/storyReadiness';
import { getThumbnailTime } from '../../utility/videoClip';

import type MuxPlayerElement from '@mux/mux-player';
import type { MuxPlayerElementEventMap } from '@mux/mux-player';
import type { AspectRatio } from '@src/types.ts/video';
import type { CSSProperties } from 'react';

const logger = LogManager.getLogger('MuxPlayerClip');

type CSSPropertiesWithVars = CSSProperties & {
  [key: `--${string}`]: string | number;
};

const playerStyle: CSSPropertiesWithVars = {
  maxHeight: '100%',
  width: '100%',
  height: '100%',
  borderRadius: 10,
  overflow: 'hidden',
  border: '1px solid var(--border)',
  aspectRatio: 16 / 9,
  '--pip-button': 'none',
  '--fullscreen-button': 'none',
  '--seek-live-button': 'none',
  '--seek-backward-button': 'none',
  '--seek-forward-button': 'none',
  '--time-range': 'none',
  '--media-object-position': 'center',
  '--time-display': 'none',
  '--duration-display': 'none',
};

/** How a clip's poster fills the player's 16:9 box for the output aspect. */
const getPosterObjectFit = (aspectRatio: AspectRatio): 'contain' | 'cover' =>
  aspectRatio === '9:16' ? 'contain' : 'cover';

export const getPlayerStyle = (
  isShowingPoster: boolean,
  aspectRatio: AspectRatio,
): CSSPropertiesWithVars => {
  return isShowingPoster
    ? {
        ...playerStyle,
        '--media-object-fit': getPosterObjectFit(aspectRatio),
      }
    : playerStyle;
};

type Props = {
  clip: {
    startTime: number;
    duration: number;
    muxPlaybackId: string | undefined;
    src: string | undefined;
    poster: string | undefined;
    autoPlay: boolean;
    suppressDerivedPoster?: boolean;
    /** Render the supplied poster without mounting Mux, even if the clip
     * still carries a placeholder playback source from the pending pipeline. */
    posterOnly?: boolean;
  };
  onPlay?: () => void;
  /** Fires when the clip's video metadata is loaded (player is usable). */
  onLoadedMetadata?: () => void;
  isPlaying?: boolean;
  aspectRatio?: AspectRatio;
  paused?: boolean;
  /** Master playback volume 0..1. Defaults to full. */
  volume?: number;
  /** Master mute. */
  muted?: boolean;
};

const MuxPlayerClip = ({
  clip,
  isPlaying,
  onPlay,
  onLoadedMetadata,
  aspectRatio,
  paused,
  volume,
  muted,
}: Props) => {
  const videoCreator = useVideoCreatorStore();
  const playerRef = useRef<MuxPlayerElement | null>(null);

  const endTime = clip.startTime + clip.duration;

  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [isShowingPoster, setIsShowingPoster] = useState(true);

  // PLAT-11445 (audio-first): while renditions are pending the clip's src is
  // the story's raw original. If this session still holds the uploaded file,
  // play that instead; a blob that errors under the player drops back to the
  // clip's own src.
  const renditionPending = isCompressedRenditionPending(videoCreator.story);
  const localPreviewUrl = useLocalPreviewVideoUrl(videoCreator.story?.id);
  const [localPreviewFailed, setLocalPreviewFailed] = useState(false);
  useEffect(() => setLocalPreviewFailed(false), [localPreviewUrl]);
  const { src, usesLocalPreview } = resolveClipPlayerSource({
    clipSrc: clip.src,
    muxPlaybackId: clip.muxPlaybackId,
    renditionPending,
    localPreviewUrl,
    localPreviewFailed,
  });

  const handleError = useCallback(
    (event: Event) => {
      logger.warn('Clip player error', {
        src,
        usesLocalPreview,
        eventType: event.type,
        ...extractErrorInfo(event),
      });
      if (usesLocalPreview) setLocalPreviewFailed(true);
    },
    [src, usesLocalPreview],
  );

  useEffect(() => {
    if (
      !isPlaying &&
      playerRef.current &&
      typeof playerRef.current.pause === 'function'
    ) {
      playerRef.current.pause();
    }
  }, [isPlaying]);

  function onMuxTimeUpdate(event: MuxPlayerElementEventMap['timeupdate']) {
    const videoElement = event.target as HTMLVideoElement;
    const seconds = videoElement.currentTime;
    if (
      seconds > clip.startTime &&
      !playerRef.current?.paused &&
      isShowingPoster
    ) {
      setIsShowingPoster(false);
    }
    setCurrentTimeMs((seconds - clip.startTime) * 1000);
    if (seconds > (endTime || 0) && isFinite(clip.startTime)) {
      playerRef.current!.pause();
      playerRef.current!.currentTime = clip.startTime;
    }
  }

  function onSeekSliderChange(newTimeMs: number) {
    playerRef.current!.currentTime = clip.startTime + newTimeMs / 1000;
  }

  let poster = clip.poster;
  // self-hosted thumbnail if not mux video to match same thumbnail after clip is saved
  // todo replace hardcoded cloudfront string. it shouldn't know anything about cloudfront
  if (!poster && !clip.muxPlaybackId && !clip.suppressDerivedPoster) {
    if (videoCreator.story?.useAws) {
      poster = getSelfHostedThumbnailUrl(
        videoCreator.story.originalVideo,
        getThumbnailTime(clip),
      );
    } else {
      poster = getMuxHostedThumbnailUrl(
        videoCreator.story?.originalVideo.video.muxPlaybackId!,
        getThumbnailTime(clip),
      );
    }
  }

  // During audio-first processing the cards intentionally have no playable
  // server source. Mounting MuxPlayer with only a local poster makes its
  // built-in "Invalid playback URL" error veil cover that valid thumbnail.
  // Keep the large local upload out of every card and render the extracted
  // poster directly until a real playback source arrives. The play button
  // lets the host mount a player for this one card only.
  if (clip.posterOnly) {
    return (
      <PlayerContainer onClick={e => e.stopPropagation()}>
        <PosterOnlyFrame>
          {poster && (
            <PosterOnlyImage
              src={poster}
              alt="Clip thumbnail"
              $fit={getPosterObjectFit(
                aspectRatio || videoCreator.getDefaultAspectRatio(),
              )}
            />
          )}
          {onPlay && (
            <PosterPlayButton
              type="button"
              aria-label="Play Clip"
              onClick={onPlay}
            >
              <CirclePlayFilledIcon width="48" height="48" fillColor="#fff" />
            </PosterPlayButton>
          )}
        </PosterOnlyFrame>
      </PlayerContainer>
    );
  }

  return (
    <PlayerContainer onClick={e => e.stopPropagation()}>
      <MuxPlayer
        ref={playerRef}
        onTimeUpdate={onMuxTimeUpdate}
        onLoadedMetadata={onLoadedMetadata}
        style={getPlayerStyle(
          isShowingPoster,
          aspectRatio || videoCreator.getDefaultAspectRatio(),
        )}
        streamType="on-demand"
        playbackId={clip.muxPlaybackId}
        startTime={clip.startTime}
        src={src}
        poster={poster}
        thumbnailTime={
          !poster && !usesLocalPreview ? getThumbnailTime(clip) : undefined
        }
        autoPlay={clip.autoPlay}
        onPlay={onPlay}
        onError={handleError}
        volume={volume ?? 1}
        muted={muted}
        paused={paused}
      />
      <ProgressBarContainer>
        <VideoSeekSlider
          max={clip.duration * 1000}
          currentTime={currentTimeMs}
          onChange={onSeekSliderChange}
          secondsPrefix="00:"
        />
      </ProgressBarContainer>
    </PlayerContainer>
  );
};

export const PlayerContainer = styled.div`
  position: relative;
  height: 100%;
`;

export const ProgressBarContainer = styled.div`
  position: absolute;
  bottom: 52px;
  width: 100%;
`;

// Same box as the player (`playerStyle`: 16:9 whatever the output aspect), so
// a card keeps its size when Play swaps the poster for MuxPlayer.
const PosterOnlyFrame = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
`;

const PosterOnlyImage = styled.img<{ $fit: 'contain' | 'cover' }>`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: ${props => props.$fit};
  object-position: center;
`;

const PosterPlayButton = styled.button`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  opacity: 0.85;

  &:hover {
    opacity: 1;
  }
`;

export default MuxPlayerClip;
